import React from 'react';

import { Popover } from '../components/Popover';
import { ToastSkin } from '../components/Toast/types';
import { Accordion } from '../components/Accordion';
import { ActionsMenuLayout } from '../components/ActionsMenuLayout';
import { SectionNotification } from '../components/SectionNotification';
import { BoxSelectionBoxSize } from '../components/BoxSelection';
import { Favorite, VideoFill } from '@wix/wix-ui-icons-common/on-stage';
import { startOfDay, endOfDay } from 'date-fns';

/*
 * This file exports object with config for components.
 * It is used for automated teskit smoke tests
 *
 * { [component.displayName]: ComponentDefinition }
 *
 * [component.displayName] = {
 *   // hook function to be called before tests begin
 *   beforeAllHook?: function;
 *
 *   // hook function to be called after tests end
 *   afterAllHook?: function;
 *
 *   // set required props, if any
 *   props?: object;
 * }
 */

const componentDefinitions = {
  Accordion: {
    props: {
      children: (
        <Accordion.Item id="1" title="some title">
          I'm a paragraph.
        </Accordion.Item>
      ),
    },
  },

  ActionsMenuLayout: {
    props: {
      children: <ActionsMenuLayout.Item onClick={() => {}} content="test" />,
    },
  },

  AddItem: {
    props: {
      iconSize: 'large',
    },
  },

  Autocomplete: {
    props: {
      options: [],
      element: {},
    },
  },

  Avatar: {
    props: {
      size: 'large',
    },
  },

  AvatarGroup: {
    props: {
      size: 'large',
    },
  },

  Badge: {
    props: {
      children: 'Badge',
    },
  },

  BoxSelection: {
    props: {
      name: 'hours',
      size: BoxSelectionBoxSize.xLarge,
      'aria-label': '',
      'aria-labelledby': '',
      onChange: () => {},
    },
  },

  Breadcrumbs: {
    props: {
      items: [
        {
          id: 0,
          value: 'Breadcrumb 1',
          href: '',
        },
        {
          id: 1,
          value: 'Breadcrumb 2',
          href: '',
        },
        {
          id: 2,
          value: 'Breadcrumb 3',
          href: '',
        },
      ],
    },
  },

  Button: {
    props: {
      children: 'button',
    },
  },

  CalendarCell: {
    props: {
      title: '11',
    },
  },

  CalendarPopover: {
    props: {
      onClose: () => {},
      closeAriaLabel: 'close',
    },
  },

  Card: {
    props: {
      stacked: true,
    },
  },

  Checkbox: {
    props: {
      onChange: () => {},
      label: 'label',
    },
  },

  CheckboxGroup: {
    props: {
      name: 'group1',
      onChange: () => {},
      label: 'Checkbox1',
    },
  },

  ColorPicker: {
    props: {
      onChange: () => {},
    },
  },

  CopyUrlButton: {
    props: {
      url: 'wix.com',
      tooltipText: 'Copy link',
      successText: 'Link Copied',
    },
  },

  Counter: {
    props: {
      inputAriaLabel: 'amount',
      onChange: () => {},
    },
  },

  CounterBadge: {
    props: {
      value: 16,
    },
  },

  CustomDialog: {
    props: {
      children: <div />,
      isOpen: false,
      onClose: () => {},
    },
  },

  DatePicker: {
    props: {
      onChange: () => {},
    },
  },

  DatePickerInput: {
    props: {
      value: new Date(),
      onChange: () => {},
    },
  },

  Dialog: {
    props: {
      isOpen: false,
      onClose: () => {},
    },
  },

  Divider: {
    props: {
      className: 'test',
    },
  },

  DotNavigation: {
    props: {
      length: 3,
    },
  },

  Dropdown: {
    props: {
      theme: 'box',
    },
  },

  ErrorMessageWrapper: {
    props: {
      disabled: false,
      error: false,
      errorMessage: '',
      inputValue: '',
      render: () => {},
    },
  },

  Event: {
    props: {
      time: '23:23',
      title: '10nis Movie!',
    },
  },

  FileUpload: {
    props: {
      label: 'File Upload',
      explanationText: 'Explanation',
      buttonText: 'Click me to upload',
    },
  },

  FloatingDropdown: {
    props: {
      options: [
        { id: '1', value: 'Most recent', isSelectable: true },
        { id: '2', value: 'Most viewed', isSelectable: true },
        { id: '3', value: 'Most liked', isSelectable: true },
        { divider: true },
        { id: '4', value: 'Most comments', isSelectable: true },
        {
          id: '5',
          value: 'Most recent posts in the whole galaxy',
          isSelectable: true,
        },
      ],
      label: 'Sort by:',
      placeholder: 'Most recent',
    },
  },

  Grid: {
    props: {
      minColumnWidth: 300,
    },
  },

  HeroImage: {
    props: {
      src: 'c5f754_186e90ba2c9b47de8a4a0a0346865b72~mv2.png',
      width: 800,
      height: 300,
    },
  },

  IconButton: {
    props: {
      icon: <Favorite />,
    },
  },

  IconToggle: {
    props: {
      icon: <div />,
    },
  },

  IconTooltip: {
    props: {
      onClick: () => {},
    },
  },

  Image: {
    props: {
      src: 'https://m.media-amazon.com/images/M/MV5BZGMwOGIwZjUtOWQ1OS00YWRjLWJmZGMtN2Y1OWQ3ZDYwYTM3XkEyXkFqcGdeQXVyNzU1NzE3NTg@._V1_.jpg',
    },
  },

  Input: {
    props: {
      value: 'test',
    },
  },

  InputDialog: {
    props: {
      isOpen: false,
    },
  },

  LegacyCard: {
    props: {
      stacked: true,
    },
  },

  LikeButton: {
    props: {
      label: 'test',
    },
  },

  MediaLabel: {
    props: {
      text: '14:30',
      icon: <VideoFill />,
    },
  },

  MobileDrawer: {
    props: {
      children: 'test',
    },
  },

  OverlappingCard: {
    props: {
      flippedRatio: true,
    },
  },

  Pagination: {
    props: {
      totalPages: 10,
    },
  },

  Picker: {
    props: {
      onPrev: () => {},
      onNext: () => {},
      value: 'October 2020',
    },
  },

  Popover: {
    props: {
      placement: 'top',
      children: (
        <>
          <Popover.Element>
            <span>element</span>
          </Popover.Element>
          <Popover.Content>
            <div>content</div>
          </Popover.Content>
        </>
      ),
    },
  },

  ProgressBar: {
    props: {
      value: 20,
    },
  },

  RadioButton: {
    props: {
      onChange: () => {},
      label: 'label',
      value: 'value',
    },
  },

  RadioButtonGroup: {
    props: {
      onChange: () => {},
      name: 'name',
    },
  },

  Range: {
    props: {
      max: 100,
    },
  },

  Ratings: {
    props: {
      value: 3,
    },
  },

  RatingsInput: {
    props: {},
  },

  ReviewSummary: {
    props: {
      items: [],
    },
  },

  Reactions: {
    props: {
      additionalReactions: [],
      reactionLabel: () => 'Heart',
      reactionIcon: () => <div />,
      popoverTriggerAriaLabel: '',
      defaultReaction: 'heart',
    },
  },

  Ribbon: {
    props: {},
  },

  SectionNotification: {
    props: {
      children: (
        <SectionNotification.Text key="unique">
          You will be redirected to payment provider's page.
        </SectionNotification.Text>
      ),
    },
  },

  ShareButton: {
    props: {
      shareData: {
        title: 'Share title',
        url: 'https://wix.com',
        text: 'some text',
      },
    },
  },

  SocialBar: {
    props: {
      tooltipText: 'Copy Link',
    },
  },

  Spinner: {
    props: {
      type: 'slim',
    },
  },

  StatesButton: {
    props: {
      idleContent: 'Click Me',
    },
  },

  StripCard: {
    props: {
      sidePadding: false,
    },
  },

  TabbedContent: {
    props: {
      items: [
        { id: '0', title: 'item 1' },
        { id: '1', title: 'item 2' },
        { id: '2', title: 'item 3' },
        { id: '3', title: 'item 4' },
      ],
      children: (id) => <div>{id}</div>,
    },
  },

  Tabs: {
    props: {
      items: [{ title: 'some-title-1' }, { title: 'some-title-2' }],
      activeTabIndex: 0,
      onTabClick: () => {},
    },
  },

  Tag: {
    props: {
      children: 'test',
    },
  },

  Tags: {
    props: {
      items: [
        { title: `Title 1`, value: `value 1` },
        { title: `Title 2`, checked: true, value: `value 2` },
      ],
      onClick: () => {},
    },
  },

  Text: {
    props: {
      children: 'test',
    },
  },

  TextArea: {
    props: {
      ariaLabel: 'test',
      value: 'test',
      onChange: () => {},
      placeholder: 'Placeholder',
      disabled: false,
    },
  },

  TextButton: {
    props: {
      children: 'test',
    },
  },

  TextField: {
    props: {
      value: 'test',
    },
  },

  ThreeDotsLoader: {
    props: {
      isStatic: true,
    },
  },

  ThumbnailImage: {
    props: {
      src: 'c5f754_f4ccb2e3ed75479dbfd55e02ef9c47e8~mv2.png',
      width: 300,
      height: 300,
    },
  },

  Toast: {
    props: {
      skin: ToastSkin.success,
    },
  },

  ToggleSwitch: {
    props: {
      checked: true,
    },
  },

  Tooltip: {
    props: {
      placement: 'bottom',
    },
  },

  WeeklyDateNavigation: {
    props: {
      onChange: () => {},
      value: {
        startOfWeek: startOfDay(new Date('2021-12-26')),
        endOfWeek: endOfDay(new Date('2022-01-01')),
      },
    },
  },

  WeeklyDatePicker: {
    props: {
      children: <div />,
    },
  },

  WowImage: {
    props: {
      children: <div />,
    },
  },
};

module.exports = componentDefinitions;
